/**
 * Registry metadata helpers for Knowledge Base artifact filtering, grouping, and doc graph links.
 * Fields align with scripts/sparc/registry_config.yaml → artifact.meta stamping.
 */

import type { Artifact } from '../types/api';

export interface RegistryMeta {
  project_id?: string;
  project_name?: string;
  keyword_tags?: string[];
  capabilities?: string[];
  client?: string;
  org_unit?: string;
  service_line?: string;
  geography?: string;
  sector?: string;
  project_status?: string;
  contract_value?: string;
  start_date?: string;
  end_date?: string;
  doc_type?: string;
  entity_type?: string;
  registry_source?: string;
  registry_href?: string;
  registry_column?: string;
  registry_resolved_path?: string;
  registry_row_hash?: string;
  registry_row_index?: number;
}

export interface RegistryFilters {
  projectId: string;
  tag: string;
  docType: string;
  client: string;
  geography: string;
  serviceLine: string;
  registrySource: string;
}

export type RegistryGroupBy =
  | 'none'
  | 'source'
  | 'type'
  | 'project_id'
  | 'doc_type'
  | 'client';

export const DEFAULT_REGISTRY_FILTERS: RegistryFilters = {
  projectId: 'all',
  tag: 'all',
  docType: 'all',
  client: 'all',
  geography: 'all',
  serviceLine: 'all',
  registrySource: 'all',
};

export const REGISTRY_GROUP_BY_OPTIONS: { value: RegistryGroupBy; label: string }[] = [
  { value: 'none', label: 'No grouping' },
  { value: 'source', label: 'Source' },
  { value: 'type', label: 'Artifact type' },
  { value: 'project_id', label: 'Project ID' },
  { value: 'doc_type', label: 'Doc type' },
  { value: 'client', label: 'Client' },
];

function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(/[,;|]/)
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
}

/** Normalize artifact meta — API uses `meta`; some legacy paths used `metadata`. */
export function getArtifactMeta(artifact: Artifact): Record<string, unknown> | null {
  const raw = artifact.meta ?? (artifact as { metadata?: Record<string, unknown> }).metadata;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as Record<string, unknown>;
}

const REGISTRY_SIGNAL_KEYS = [
  'registry_source',
  'project_id',
  'doc_type',
  'registry_row_hash',
  'registry_href',
  'registry_column',
  'client',
  'client_name',
  'geography',
  'service_line',
  'keyword_tags',
  'capabilities',
  'project_name',
  'entity_type',
] as const;

function hasRegistrySignalInMeta(meta: Record<string, unknown>): boolean {
  return REGISTRY_SIGNAL_KEYS.some((key) => {
    const value = meta[key];
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
}

export function extractRegistryMeta(artifact: Artifact): RegistryMeta | null {
  const meta = getArtifactMeta(artifact);
  if (!meta) return null;

  const hasRegistrySignal = hasRegistrySignalInMeta(meta);

  if (!hasRegistrySignal) return null;

  return {
    project_id: meta.project_id ? String(meta.project_id) : undefined,
    project_name: meta.project_name ? String(meta.project_name) : undefined,
    keyword_tags: asStringArray(meta.keyword_tags),
    capabilities: asStringArray(meta.capabilities),
    client: meta.client ? String(meta.client) : meta.client_name ? String(meta.client_name) : undefined,
    org_unit: meta.org_unit
      ? String(meta.org_unit)
      : meta.sparc_entity
        ? String(meta.sparc_entity)
        : undefined,
    service_line: meta.service_line ? String(meta.service_line) : undefined,
    geography: meta.geography ? String(meta.geography) : undefined,
    sector: meta.sector ? String(meta.sector) : undefined,
    project_status: meta.project_status ? String(meta.project_status) : undefined,
    contract_value: meta.contract_value ? String(meta.contract_value) : undefined,
    start_date: meta.start_date ? String(meta.start_date) : undefined,
    end_date: meta.end_date ? String(meta.end_date) : undefined,
    doc_type: meta.doc_type ? String(meta.doc_type) : undefined,
    entity_type: meta.entity_type ? String(meta.entity_type) : undefined,
    registry_source: meta.registry_source ? String(meta.registry_source) : undefined,
    registry_href: meta.registry_href ? String(meta.registry_href) : undefined,
    registry_column: meta.registry_column ? String(meta.registry_column) : undefined,
    registry_resolved_path: meta.registry_resolved_path
      ? String(meta.registry_resolved_path)
      : undefined,
    registry_row_hash: meta.registry_row_hash ? String(meta.registry_row_hash) : undefined,
    registry_row_index:
      meta.registry_row_index !== undefined && meta.registry_row_index !== null
        ? Number(meta.registry_row_index)
        : undefined,
  };
}

export function hasRegistryMeta(artifact: Artifact): boolean {
  return extractRegistryMeta(artifact) !== null;
}

export function countActiveRegistryFilters(filters: RegistryFilters): number {
  return Object.values(filters).filter((v) => v !== 'all').length;
}

function matchesFilterValue(fieldValue: string | undefined, filterValue: string): boolean {
  if (filterValue === 'all') return true;
  if (!fieldValue) return false;
  return fieldValue.toLowerCase() === filterValue.toLowerCase();
}

export function applyRegistryFilters(
  artifacts: Artifact[],
  filters: RegistryFilters,
): Artifact[] {
  if (countActiveRegistryFilters(filters) === 0) return artifacts;

  return artifacts.filter((artifact) => {
    const reg = extractRegistryMeta(artifact);
    if (!reg) return false;

    if (!matchesFilterValue(reg.project_id, filters.projectId)) return false;
    if (!matchesFilterValue(reg.doc_type, filters.docType)) return false;
    if (!matchesFilterValue(reg.client, filters.client)) return false;
    if (!matchesFilterValue(reg.geography, filters.geography)) return false;
    if (!matchesFilterValue(reg.service_line, filters.serviceLine)) return false;
    if (!matchesFilterValue(reg.registry_source, filters.registrySource)) return false;

    if (filters.tag !== 'all') {
      const tags = (reg.keyword_tags ?? []).map(normalizeTag);
      if (!tags.includes(normalizeTag(filters.tag))) return false;
    }

    return true;
  });
}

export interface RegistryFilterOptions {
  projectIds: string[];
  tags: string[];
  docTypes: string[];
  clients: string[];
  geographies: string[];
  serviceLines: string[];
  registrySources: string[];
}

function uniqueSorted(values: (string | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => !!v && v.trim().length > 0))).sort(
    (a, b) => a.localeCompare(b),
  );
}

export function getRegistryFilterOptions(artifacts: Artifact[]): RegistryFilterOptions {
  const projectIds: string[] = [];
  const tags: string[] = [];
  const docTypes: string[] = [];
  const clients: string[] = [];
  const geographies: string[] = [];
  const serviceLines: string[] = [];
  const registrySources: string[] = [];

  for (const artifact of artifacts) {
    const reg = extractRegistryMeta(artifact);
    if (!reg) continue;
    if (reg.project_id) projectIds.push(reg.project_id);
    if (reg.doc_type) docTypes.push(reg.doc_type);
    if (reg.client) clients.push(reg.client);
    if (reg.geography) geographies.push(reg.geography);
    if (reg.service_line) serviceLines.push(reg.service_line);
    if (reg.registry_source) registrySources.push(reg.registry_source);
    if (reg.keyword_tags?.length) tags.push(...reg.keyword_tags);
  }

  return {
    projectIds: uniqueSorted(projectIds),
    tags: uniqueSorted(tags),
    docTypes: uniqueSorted(docTypes),
    clients: uniqueSorted(clients),
    geographies: uniqueSorted(geographies),
    serviceLines: uniqueSorted(serviceLines),
    registrySources: uniqueSorted(registrySources),
  };
}

export function getRegistryGroupKey(
  artifact: Artifact,
  groupBy: RegistryGroupBy,
  getSourceName: (artifact: Artifact) => string,
): string {
  switch (groupBy) {
    case 'source':
      return getSourceName(artifact);
    case 'type':
      return artifact.artifact_type || 'Unknown type';
    case 'project_id': {
      const reg = extractRegistryMeta(artifact);
      return reg?.project_id || 'No project ID';
    }
    case 'doc_type': {
      const reg = extractRegistryMeta(artifact);
      return reg?.doc_type || 'No doc type';
    }
    case 'client': {
      const reg = extractRegistryMeta(artifact);
      return reg?.client || 'No client';
    }
    case 'none':
    default:
      return 'All Artifacts';
  }
}

export function groupArtifactsByRegistryField(
  artifacts: Artifact[],
  groupBy: RegistryGroupBy,
  getSourceName: (artifact: Artifact) => string,
): Record<string, Artifact[]> {
  if (groupBy === 'none') {
    return { 'All Artifacts': artifacts };
  }

  const grouped: Record<string, Artifact[]> = {};
  for (const artifact of artifacts) {
    const key = getRegistryGroupKey(artifact, groupBy, getSourceName);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(artifact);
  }

  return Object.fromEntries(
    Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)),
  );
}

/** Artifacts sharing the same registry row (project + row hash), excluding self. */
export function getRelatedRegistryArtifacts(
  artifact: Artifact,
  allArtifacts: Artifact[],
): Artifact[] {
  const reg = extractRegistryMeta(artifact);
  if (!reg?.project_id) return [];

  const rowHash = reg.registry_row_hash;
  const projectId = reg.project_id;

  return allArtifacts.filter((other) => {
    if (other.id === artifact.id) return false;
    const otherReg = extractRegistryMeta(other);
    if (!otherReg?.project_id || otherReg.project_id !== projectId) return false;
    if (rowHash && otherReg.registry_row_hash) {
      return otherReg.registry_row_hash === rowHash;
    }
    return true;
  });
}

export function formatDocTypeLabel(docType: string | undefined): string {
  if (!docType) return 'Document';
  return docType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Humanize registry_column / hyperlink spec keys from credential sheet manifests. */
export function formatRegistryColumnLabel(column: string | undefined): string {
  if (!column) return 'Document link';
  const normalized = column.trim().toLowerCase();
  const shortcuts: Record<string, string> = {
    wo: 'Work Order',
    cc: 'Completion Certificate',
    agmt: 'Agreement',
    rfp: 'RFP Document',
  };
  if (shortcuts[normalized]) return shortcuts[normalized];
  return formatDocTypeLabel(column);
}

export function formatProjectGroupLabel(projectId: string, projectName?: string): string {
  if (!projectName?.trim()) return projectId;
  const trimmed = projectName.trim();
  const maxLen = 48;
  const shortName = trimmed.length > maxLen ? `${trimmed.slice(0, maxLen)}…` : trimmed;
  return `${projectId} — ${shortName}`;
}

/** Prefer project_name from any artifact in a project_id group for header display. */
export function getProjectGroupDisplayLabel(artifacts: Artifact[], projectId: string): string {
  for (const artifact of artifacts) {
    const reg = extractRegistryMeta(artifact);
    if (reg?.project_id === projectId && reg.project_name) {
      return formatProjectGroupLabel(projectId, reg.project_name);
    }
  }
  return projectId;
}

export function isExternalRegistryHref(href: string): boolean {
  return /^https?:\/\//i.test(href.trim());
}
